import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box, useToast } from '@cad-arc';
import { EditorControl } from '../../../common/components/lexical';
import { OnLexicalError } from '../../../common/components/lexical/types';
import { AddMessageInput } from './add-message-input';
import { useMessagingStore } from '../../hooks/use-messaging-store';
import { errorOccurredWhile } from '../../../common/helpers/error-occurred-while';
import { observer } from 'mobx-react';
import { useAddMessage } from '../../hooks/resource/use-add-message';

export interface AddMessageFormProps {
    disabledText?: string;
    onLexicalError?: OnLexicalError;
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
}

type AddMessageFormType = {
    addMessage: string;
};

export const AddMessageForm = observer(
    ({ disabledText, editorControlRef }: AddMessageFormProps): React.JSX.Element => {
        const { selectedConversationKey, isAddUrgentMessageEnabled } = useMessagingStore();
        const { addMessage } = useAddMessage();

        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const { handleSubmit, reset, control } = useForm<AddMessageFormType>({
            defaultValues: { addMessage: '' },
        });
        const toast = useToast();

        const onSubmit: SubmitHandler<AddMessageFormType> = async (form) => {
            try {
                setIsSubmitting(true);
                editorControlRef.current?.setEditable(false);
                if (
                    selectedConversationKey === undefined ||
                    selectedConversationKey.participantIdentity === undefined
                ) {
                    throw new Error('Selected sender participant not found');
                }
                const text = form.addMessage;
                await addMessage({
                    isUrgent: isAddUrgentMessageEnabled,
                    senderParticipant: selectedConversationKey.participantIdentity,
                    text: typeof text === 'string' ? text.trim() : text,
                    conversationKey: selectedConversationKey,
                });
                reset();
                editorControlRef.current?.clear();
                editorControlRef.current?.focus();
            } catch (error) {
                editorControlRef.current?.focus();
                toast({
                    status: 'error',
                    description: errorOccurredWhile('sending a message'),
                });
            } finally {
                setIsSubmitting(false);
                editorControlRef.current?.setEditable(true);
            }
        };

        return (
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box
                    p="var(--arc-space-px) var(--arc-space-3) var(--arc-space-3) var(--arc-space-3)"
                    w="100%"
                    m={0}
                >
                    <AddMessageInput
                        control={control}
                        name="addMessage"
                        editorControlRef={editorControlRef}
                        isSubmitting={isSubmitting}
                        onSubmit={handleSubmit(onSubmit)}
                        disabledText={disabledText}
                    />
                    <input type="submit" hidden />
                </Box>
            </form>
        );
    }
);
